import { useState } from "react"
import { Box, Typography } from "@mui/material"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faComment } from "@fortawesome/pro-light-svg-icons"
import { faCircle } from "@fortawesome/pro-solid-svg-icons"
import { makeStyles } from "tss-react/mui"

import { useDateTime } from "../../../modules/localization/useDateTime"
import { useAppLocalization } from "../../../modules/localization/components/AppLocalizationProvider"
import { LocalizedStrict } from "../../../modules/localization/components/AppLocalized"
import {
    ChatMessage,
    ChatMessageAuthorRole,
    ChatMessageSender,
    MessageDirection,
} from "../orderChat/orderChatTypes"
import { useOrderChat } from "../orderChat/useOrderChat"
import { OrderChatInput } from "../orderChat/OrderChatInput"
import { OrderChatDisclaimer, isDisclaimerAccepted } from "../orderChat/OrderChatDisclaimer"
import { DrawerSection } from "./DrawerSection"

const useStyles = makeStyles()((theme) => ({
    repliedLabel: {
        fontWeight: theme.typography.fontWeightMedium,
        color: theme.palette.tailwind.slate[400],
    },
    chatArea: {
        padding: ".75rem 1rem 1rem",
    },
}))

const useChatStyles = makeStyles()((theme) => ({
    root: {
        display: "flex",
        flexDirection: "column",
        gap: ".75rem",
    },
    list: {
        display: "flex",
        flexDirection: "column",
        gap: ".75rem",
        maxHeight: "18rem",
        overflowY: "auto",
    },
    row: {
        display: "flex",
        flexDirection: "column",
        maxWidth: "85%",
    },
    rowOwn: {
        alignSelf: "flex-end",
        alignItems: "flex-end",
    },
    meta: {
        fontWeight: theme.typography.fontWeightMedium,
        color: theme.palette.tailwind.slate[400],
        marginBottom: ".25rem",
    },
    metaName: {
        color: "inherit",
    },
    cellIconDivider: {
        margin: "0 .5rem",
    },
    bubble: {
        padding: ".5rem .75rem",
        borderRadius: ".75rem .75rem .75rem .25rem",
        background: theme.palette.tailwind.slate[100],
        color: theme.palette.tailwind.slate[800],
        whiteSpace: "pre-wrap",
        overflowWrap: "anywhere",
    },
    bubbleOwn: {
        borderRadius: ".75rem .75rem .25rem .75rem",
        background: theme.palette.tailwind.emerald[50],
        color: theme.palette.tailwind.emerald[900],
    },
    empty: {
        padding: "1.5rem 0",
        textAlign: "center",
        color: theme.palette.tailwind.slate[400],
    },
}))

type OrderMessageContentProps = Readonly<{
    sender?: ChatMessageSender
    viewerRole: ChatMessageAuthorRole
    messages: ChatMessage[]
    onSend: (body: string) => void
}>

function OrderMessageContent({ sender, viewerRole, messages, onSend }: OrderMessageContentProps) {
    const { classes, cx } = useChatStyles()
    const { l10n } = useAppLocalization()
    const dateTimeFactory = useDateTime()
    const [accepted, setAccepted] = useState(isDisclaimerAccepted)

    const placeholder = l10n.getString(
        viewerRole === ChatMessageAuthorRole.MERCHANT
            ? "order-chat-placeholder-merchant"
            : "order-chat-placeholder-customer",
    )

    const formatMeta = (created: string) => {
        const dt = dateTimeFactory.fromApi(created)
        if (!dt.isValid) return ""
        return `${dt.toRelativeCalendar() ?? ""} ${dateTimeFactory.formatTime(dt)}`.trim()
    }

    // Bubbles are drawer-specific (the shared OrderChatMessageList is left untouched); the input reuses OrderChatInput.
    // Without a sender the viewer can't post (read-only portals), so the disclaimer + input are hidden.
    return (
        <Box className={classes.root}>
            {sender && !accepted && <OrderChatDisclaimer onAccept={() => setAccepted(true)} />}
            <Box className={classes.list}>
                {messages.length === 0 && (
                    <LocalizedStrict id="meeting-service-order-drawer-messages-empty">
                        <Typography variant="body2" className={classes.empty}>
                            No messages yet
                        </Typography>
                    </LocalizedStrict>
                )}
                {messages.map((message) => {
                    const own = message.messageDirection === MessageDirection.SENT
                    return (
                        <Box key={message.id} className={cx(classes.row, own && classes.rowOwn)}>
                            <Typography component="div" variant="caption" className={classes.meta}>
                                <Box component="span" className={classes.metaName}>
                                    {message.creator.displayName}
                                </Box>
                                <FontAwesomeIcon className={classes.cellIconDivider} width=".25rem" icon={faCircle} />
                                {formatMeta(message.created)}
                            </Typography>
                            <Box className={cx(classes.bubble, own && classes.bubbleOwn)}>
                                <Typography variant="body2">{message.body}</Typography>
                            </Box>
                        </Box>
                    )
                })}
            </Box>
            {sender && <OrderChatInput onSend={onSend} disabled={!accepted} placeholder={placeholder} />}
        </Box>
    )
}

type Props = Readonly<{
    orderId: number
    sender?: ChatMessageSender
    viewerRole: ChatMessageAuthorRole
    open: boolean
    onToggle: () => void
}>

export function OrderMessageSection({ orderId, sender, viewerRole, open, onToggle }: Props) {
    const { classes } = useStyles()
    const { messages, sendMessage } = useOrderChat(orderId, sender)
    // useOrderChat sorts oldest first, so the reply is the last entry.
    const hasReply = messages[messages.length - 1]?.messageDirection === MessageDirection.SENT
    const isMerchantViewer = viewerRole === ChatMessageAuthorRole.MERCHANT

    return (
        <DrawerSection
            icon={faComment}
            titleId={
                isMerchantViewer
                    ? "meeting-service-order-drawer-chat-merchant"
                    : "meeting-service-order-drawer-chat-customer"
            }
            titleFallback={isMerchantViewer ? "Chat with customer" : "Chat with canteen"}
            badge={
                hasReply ? (
                    <LocalizedStrict id="meeting-service-order-drawer-replied">
                        <Typography
                            component="span"
                            variant="caption"
                            className={classes.repliedLabel}
                        >
                            Replied
                        </Typography>
                    </LocalizedStrict>
                ) : undefined
            }
            open={open}
            onToggle={onToggle}
        >
            <Box className={classes.chatArea}>
                <OrderMessageContent
                    sender={sender}
                    viewerRole={viewerRole}
                    messages={messages}
                    onSend={sendMessage}
                />
            </Box>
        </DrawerSection>
    )
}
